import { useMemo, useState } from "react";
import { AsyncSelectOption } from "@/components/AsyncSelect";
import FilterBuilder from "@/components/filter/FilterBuilder";
import { FilterRecord } from "@/components/filter/filter-constant";
import { useActiveComponentCountController } from "@/components/filter/use-ActiveComponentCountController";
import { useDebounce } from "@/hooks/use-debounce";
import { useProviderListV2 } from "@/queries/use-provider-list-v2";
import { DEF_PAGINATION, getXtenantId } from "@/utils/utils";
import {
  AUDIT_LOG_DATE_RANGE_KEYS,
  AUDIT_LOG_DATE_RANGE_OPTIONS,
  AuditLogFilters,
} from "../practice-setting-constant";

interface AuditLogFilterProps {
  filters: AuditLogFilters;
  setAuditLogFilters: (auditLogFilters: AuditLogFilters) => void;
}

const AuditLogFilter = ({
  filters,
  setAuditLogFilters,
}: AuditLogFilterProps) => {
  const activeFilterCount = useActiveComponentCountController(filters);
  const [filterOpened, setFilterOpened] = useState(false);

  const [searchKey, setSearchKey] = useDebounce<string>("");
  const { data: providersList } = useProviderListV2({
    pagination: DEF_PAGINATION,
    searchString: searchKey,
    xTENANTID: getXtenantId(),
    isReferral: false,
    enabled: filterOpened,
  });

  const options: AsyncSelectOption[] = useMemo(() => {
    return (
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })) ?? []
    );
  }, [providersList?.data?.content]);

  const filtersProvider: FilterRecord = {
    date_range: {
      type: "date_range_preset",
      label: "Date Range",
      childKeys: [...AUDIT_LOG_DATE_RANGE_KEYS],
      options: [...AUDIT_LOG_DATE_RANGE_OPTIONS],
    },
    patient: {
      type: "text",
      label: "Patient",
      placeholder: "Search Patient",
    },
    user: {
      type: "select",
      label: "User",
      options: [...(options ?? []), { value: "admin", label: "Admin" }],
      placeholder: "Search User",
      onSearch: (value) => setSearchKey(value),
    },
    category: {
      type: "select",
      label: "Category",
      options: categoryOptions,
      placeholder: "Select Category",
    },
    action: {
      type: "select",
      label: "Action",
      options: actionOptions,
      placeholder: "Select Action",
    },
    logType: {
      type: "select",
      label: "Log Type",
      options: logTypeOptions,
      placeholder: "Select Log Type",
    },
    ipAddress: {
      type: "text",
      label: "IP Address",
      placeholder: "Enter IP Address",
    },
  };

  return (
    <div onClick={() => !filterOpened && setFilterOpened(true)}>
      <FilterBuilder
        filters={filtersProvider}
        onApply={(v) => setAuditLogFilters(v)}
        prefillValues={filters}
        activeFilterCount={activeFilterCount}
        h="24.625rem"
      />
    </div>
  );
};

export default AuditLogFilter;

const categoryOptions = [
  { value: "account", label: "Account" },
  { value: "administrative", label: "Administrative" },
  { value: "assessment", label: "Assessment" },
  { value: "authentication", label: "Authentication" },
  { value: "billing", label: "Billing" },
  { value: "clinical", label: "Clinical" },
  { value: "clinical documentation", label: "Clinical Documentation" },
  { value: "communication", label: "Communication" },
  { value: "encounter", label: "Visit" },
  { value: "financial", label: "Financial" },
  { value: "integration", label: "Integration" },
  { value: "provider group Management", label: "Provider Group Management" },
  { value: "provider onboarding", label: "Provider Onboarding" },
  { value: "scheduling", label: "Scheduling" },
  { value: "system", label: "System" },
  { value: "workflow", label: "Workflow" },
];

const logTypeOptions = [
  { value: "login", label: "Sign In" },
  { value: "logout", label: "Sign Out" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "Account", label: "Account" },
  { value: "Administrative", label: "Administrative" },
  { value: "Appointment", label: "Appointment" },
  { value: "Assessment", label: "Assessment" },
  { value: "Availability", label: "Availability" },
  { value: "Billing", label: "Billing" },
  { value: "Billing Management", label: "Billing Management" },
  { value: "Catalog", label: "Catalog" },
  { value: "Claim", label: "Claim" },
  { value: "Claims Management", label: "Claims Management" },
  { value: "Clinical", label: "Clinical" },
  { value: "Clinical Documentation", label: "Clinical Documentation" },
  { value: "Communication", label: "Communication" },
  { value: "Configuration", label: "Configuration" },
  { value: "Department", label: "Department" },
  { value: "Education", label: "Education" },
  { value: "Financial", label: "Financial" },
  { value: "Flag", label: "Flag" },
  { value: "Location", label: "Location" },
  { value: "Notification", label: "Notification" },
  { value: "Order Management", label: "Order Management" },
  { value: "Patient", label: "Patient" },
  { value: "Patient Flag", label: "Patient Flag" },
  { value: "Patient Registration", label: "Patient Registration" },
  { value: "Payment", label: "Payment" },
  { value: "Prescription", label: "Prescription" },
  { value: "Profile", label: "Profile" },
  { value: "Provider", label: "Provider" },
  { value: "Provider Group", label: "Provider Group" },
  { value: "Provider Onboarding", label: "Provider Onboarding" },
  { value: "Referral", label: "Referral" },
  { value: "Referring Agency", label: "Referring Agency" },
  { value: "Result", label: "Result" },
  { value: "Role", label: "Role" },
  { value: "Scheduling", label: "Scheduling" },
  { value: "Staff", label: "Staff" },
  { value: "System", label: "System" },
  { value: "Task", label: "Task" },
  { value: "Task Management", label: "Task Management" },
  { value: "Workflow", label: "Workflow" },
];

const actionOptions = [
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "view", label: "View" },
  { value: "download", label: "Download" },
  { value: "failed_attempt", label: "Failed Attempt" },
  { value: "reset_password", label: "Reset Password" },
];
